import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAuthUserContext } from '@/modules/auth';
import { FunctionComponent, useEffect, useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';

import { Alert, Link, Collapse, Button } from '@mui/material';
import { useLogout } from '../hooks/useLogout';
import { CopyrightText } from '../components/CopyrightText';
import LoginLayout from '../layouts/LoginLayout';
import LoginLayoutBody from '../components/LoginLayoutBody';
import { useLogin } from '../hooks/useLogin';

export const LoginPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const { signIn, authError } = useAuthUserContext();
  const { logOut } = useLogout();
  const [searchParams] = useSearchParams();

  const { logIn } = useLogin();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    try {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      const credentials = {
        email: data.get('email')?.toString() ?? '',
        password: data.get('password')?.toString() ?? '',
      };

      const loggedInUser = await signIn(credentials.email, credentials.password);

      await logIn(loggedInUser.uid);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('logOut')) {
      logOut();
    }
  }, []);

  function onRegisterClicked() {
    navigate('/register/create-your-account');
  }

  return (
    <LoginLayout>
      <LoginLayoutBody>
        <div className="title-font mb-14 flex items-center  font-medium text-inherit no-underline md:mb-14">
          <svg
            className="w-48 "
            width="388"
            height="133"
            viewBox="0 0 388 133"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            shapeRendering={'geometricPrecision'}
          >
            <path
              d="M141.792 101.968C141.109 109.477 137.995 116.005 132.448 121.552C126.987 127.013 120.501 130.085 112.992 130.768H110.304C106.635 130.768 103.221 130.341 100.064 129.488C96.992 128.549 94.048 126.971 91.232 124.752C87.0507 121.339 83.936 117.499 81.888 113.232C79.9253 108.88 78.944 104.059 78.944 98.768L79.328 92.496C80.7787 85.584 84.3627 79.952 90.08 75.6C95.8827 71.1627 102.197 68.944 109.024 68.944C111.328 68.944 113.76 69.2853 116.32 69.968C118.88 70.6507 121.312 71.632 123.616 72.912V36.816H141.792V101.968ZM124.768 99.664C124.768 95.824 123.403 92.496 120.672 89.68C117.941 86.864 114.656 85.456 110.816 85.456C106.976 85.456 103.648 86.8213 100.832 89.552C98.016 92.2827 96.608 95.568 96.608 99.408C96.608 103.248 97.9733 106.576 100.704 109.392C103.435 112.208 106.72 113.616 110.56 113.616C114.4 113.616 117.728 112.251 120.544 109.52C123.36 106.789 124.768 103.504 124.768 99.664ZM196.429 130.128H161.997C159.352 129.872 156.962 128.677 154.829 126.544C153.037 124.752 151.501 122.405 150.221 119.504V37.456H169.293V112.208H196.429V130.128ZM265.661 130.128H248.253V125.776C245.949 127.483 243.346 128.805 240.445 129.744C237.629 130.597 234.941 131.024 232.381 131.024C224.53 131.024 217.704 127.952 211.901 121.808C206.184 115.579 203.325 108.325 203.325 100.048C203.325 91.9413 206.482 84.688 212.797 78.288C219.112 71.888 226.194 68.688 234.045 68.688C242.493 68.688 249.874 71.504 256.189 77.136C262.504 82.768 265.661 89.7227 265.661 98V130.128ZM248.509 100.944C248.509 97.104 247.101 93.8187 244.285 91.088C241.469 88.3573 238.141 86.992 234.301 86.992C230.461 86.992 227.176 88.4 224.445 91.216C221.714 94.032 220.349 97.36 220.349 101.2C220.349 105.04 221.757 108.325 224.573 111.056C227.389 113.787 230.717 115.152 234.557 115.152C238.397 115.152 241.682 113.744 244.413 110.928C247.144 108.112 248.509 104.784 248.509 100.944ZM336.313 98.512C336.313 103.888 335.332 108.709 333.369 112.976C331.492 117.157 328.377 120.997 324.025 124.496C321.38 126.544 318.393 128.08 315.065 129.104C311.737 130.043 308.366 130.512 304.953 130.512H302.265C294.841 129.829 288.356 126.757 282.809 121.296C277.262 115.835 274.148 109.307 273.465 101.712V36.56H291.641V72.656C293.86 71.376 296.249 70.3947 298.809 69.712C301.369 69.0293 303.844 68.688 306.233 68.688C313.06 68.688 319.332 70.9067 325.049 75.344C330.852 79.696 334.478 85.328 335.929 92.24L336.313 98.512ZM318.649 99.152C318.649 95.312 317.241 92.0267 314.425 89.296C311.609 86.5653 308.281 85.2 304.441 85.2C300.601 85.2 297.316 86.608 294.585 89.424C291.854 92.24 290.489 95.568 290.489 99.408C290.489 103.248 291.897 106.533 294.713 109.264C297.529 111.995 300.857 113.36 304.697 113.36C308.537 113.36 311.822 111.952 314.553 109.136C317.284 106.32 318.649 102.992 318.649 99.152ZM384.315 112.976C384.315 119.035 381.798 123.856 376.763 127.44C372.326 130.597 366.907 132.176 360.507 132.176C357.35 132.176 354.192 131.707 351.035 130.768C347.878 129.829 345.104 128.507 342.715 126.8V110.288C345.616 112.165 348.176 113.573 350.395 114.512C353.723 115.963 356.752 116.688 359.483 116.688C361.872 116.688 363.792 116.389 365.243 115.792C366.694 115.195 367.419 114 367.419 112.208C367.419 110.757 363.152 108.325 354.619 104.912C346.171 101.413 341.947 95.8667 341.947 88.272C341.947 82.2987 344.038 77.3067 348.219 73.296C352.486 69.2 358.118 67.152 365.115 67.152C368.443 67.152 371.387 67.4507 373.947 68.048C376.592 68.56 379.494 69.456 382.651 70.736V88.272C380.688 86.5653 378.299 85.1147 375.483 83.92C372.752 82.64 370.064 82 367.419 82C365.286 82 363.366 82.4267 361.659 83.28C360.038 84.048 359.227 85.2427 359.227 86.864C359.227 88.3147 363.408 90.9173 371.771 94.672C380.134 98.4267 384.315 104.528 384.315 112.976Z"
              fill="#172554"
            />
            <path d="M49 40H29V60L0 130H20L39 84L58 130H78L49 60V40Z" fill="#1E40AF" />
            <circle cx="64" cy="25" r="15" fill="#172554" />
            <circle cx="10" cy="40" r="10" fill="#3B82F6" />
            <circle cx="29" cy="10" r="10" fill="#3B82F6" />
            <path d="M49 60L78 130H58L39 84L49 60Z" fill="#3B82F6" />
            <path d="M29 60L0 130H20L39 84L29 60Z" fill="#172554" />
          </svg>
        </div>
        <Typography component="h1" variant="h5">
          Sign In
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            disabled={isLoading}
            margin="normal"
            size="medium"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
          />
          <TextField
            disabled={isLoading}
            margin="normal"
            size="medium"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
          />
          <div className="flex justify-end">
            <Link component={RouterLink} to={'/reset-password'} className="text-gray-600">
              Forgotten Password?
            </Link>
          </div>
          {/* <FormControlLabel control={<Checkbox value="remember" color="primary" />} label="Remember me" /> */}
          <Collapse in={authError !== null}>
            <Alert variant="standard" severity="warning" className="mt-4">
              {authError}
            </Alert>
          </Collapse>

          <LoadingButton loading={isLoading} type="submit" size="large" fullWidth variant="contained" className="flex h-14 mt-4">
            Sign In
          </LoadingButton>

          <div className="my-6">{/* <Divider /> */}</div>
          <div className="flex justify-center mb-4">
            <Button variant="outlined" fullWidth size="large" onClick={onRegisterClicked} className="h-14">
              Create New Account
            </Button>
          </div>

          <CopyrightText />
          {/* <Grid container>
            <Grid item xs>
              <Link href="#" variant="body2">
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              <Link href="#" variant="body2">
                {"Don't have an account? Sign Up"}
              </Link>
            </Grid>
          </Grid> */}
        </Box>
      </LoginLayoutBody>
    </LoginLayout>
  );
};
